import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
} from '@angular/core';
import Swiper, {SwiperOptions} from 'swiper';

/**
 * Thin wrapper around Swiper core, replacing ngx-swiper-wrapper (a
 * ViewEngine-era library that cannot be loaded from Angular 16 onwards).
 */
@Directive({
    selector: '[swiper]',
    standalone: false,
})
export class SwiperDirective implements AfterViewInit, OnDestroy {
    @Input('swiper')
    public options: SwiperOptions = {};

    @Output()
    public slideChangeTransitionEnd = new EventEmitter<void>();

    private instance?: Swiper;
    private initialIndex?: number;

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private zone: NgZone
    ) {}

    public ngAfterViewInit(): void {
        const options =
            this.initialIndex !== undefined
                ? {...this.options, initialSlide: this.initialIndex}
                : this.options;
        this.initialIndex = undefined;

        // Swiper's own listeners must not trigger change detection
        this.zone.runOutsideAngular(() => {
            this.instance = new Swiper(this.elementRef.nativeElement, options);
        });
        this.instance?.on('slideChangeTransitionEnd', () =>
            this.zone.run(() => this.slideChangeTransitionEnd.emit())
        );
    }

    public ngOnDestroy(): void {
        this.instance?.destroy(true, true);
        this.instance = undefined;
    }

    public getIndex(real = false): number {
        if (!this.instance) {
            return this.initialIndex ?? 0;
        }

        return real ? this.instance.realIndex : this.instance.activeIndex;
    }

    public setIndex(index: number, speed?: number, silent?: boolean): void {
        if (!this.instance) {
            this.initialIndex = index;
            return;
        }

        let realIndex = index * (this.instance.params.slidesPerGroup ?? 1);
        if (this.instance.params.loop) {
            realIndex += this.instance.loopedSlides ?? 0;
        }

        this.zone.runOutsideAngular(() => {
            this.instance?.slideTo(realIndex, speed, !silent);
        });
    }

    public nextSlide(speed?: number, silent?: boolean): void {
        if (this.instance) {
            this.zone.runOutsideAngular(() => {
                this.instance?.slideNext(speed, !silent);
            });
        }
    }

    public prevSlide(speed?: number, silent?: boolean): void {
        if (this.instance) {
            this.zone.runOutsideAngular(() => {
                this.instance?.slidePrev(speed, !silent);
            });
        }
    }

    public update(): void {
        setTimeout(() => {
            if (this.instance) {
                this.zone.runOutsideAngular(() => {
                    this.instance?.update();
                });
            }
        }, 0);
    }
}
